/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/mappers.ts (Agents)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Mapping utilities for converting between API responses and form state.

────────────────────────────────────────────────────────────────*/

import {
  FormState,
  AgentResponse,
  MappedFormState,
  AgentStatus,
  AgentType,
  AgentStateMode
} from '../types';
import { KNOWN_SETTINGS_KEYS } from '../constants';
import {
  getStringSetting,
  getBooleanSetting,
  getNumberSetting,
  extractExtraSettings
} from './settingsHelpers';

/**
 * Map API response to form state
 */
export function mapResponseToState(agent: AgentResponse): MappedFormState {
  const settings =
    agent.settings && typeof agent.settings === 'object'
      ? (agent.settings as Record<string, unknown>)
      : {};

  const extraSettings = extractExtraSettings(settings);

  const asStatus = (value: unknown, fallback: AgentStatus): AgentStatus => {
    if (typeof value === 'string') {
      if (['Available', 'Available (On Demand)', 'Logged Out', 'On Break'].includes(value)) {
        return value as AgentStatus;
      }
    }
    return fallback;
  };

  const asAgentType = (value: unknown, fallback: AgentType): AgentType => {
    if (typeof value === 'string') {
      if (['Inbound', 'Outbound', 'Callback'].includes(value)) {
        return value as AgentType;
      }
    }
    return fallback;
  };

  const asStateMode = (value: unknown, fallback: AgentStateMode): AgentStateMode => {
    if (typeof value === 'string') {
      if (['auto', 'manual'].includes(value)) {
        return value as AgentStateMode;
      }
    }
    return fallback;
  };

  const formState: FormState = {
    // General
    agentName: agent.name ?? '',
    extension: agent.extension ?? '',
    enabled: Boolean(agent.enabled),
    initialStatus: asStatus(agent.status, 'Logged Out'),
    stateMode: asStateMode(settings.state_mode, 'auto'),
    agentType: asAgentType(settings.agent_type, 'Inbound'),
    recordCalls: getBooleanSetting(settings, 'record_calls', false),
    maxConcurrentCalls: String(getNumberSetting(settings, 'max_calls', 1)),
    
    // Behavior
    maxNoAnswer: String(getNumberSetting(settings, 'max_no_answer', 3)),
    busyDelayTime: String(getNumberSetting(settings, 'busy_delay_time', 90)),
    rejectDelayTime: String(getNumberSetting(settings, 'reject_delay_time', 90)),
    noAnswerDelayTime: String(getNumberSetting(settings, 'no_answer_delay_time', 30)),
    contactTimeout: String(getNumberSetting(settings, 'contact_timeout', 20)),
    wrapUpTime: String(getNumberSetting(settings, 'wrap_up_time', 10)),
    
    // Advanced
    rejectMultipleCalls: getBooleanSetting(settings, 'reject_multiple_calls', false),
    skipAgentWhileBusy: getBooleanSetting(settings, 'skip_agent_while_busy', false),
    agentCooldown: String(getNumberSetting(settings, 'agent_cooldown', 0)),
    ignoreBusyAfterTimeout: getBooleanSetting(settings, 'ignore_busy_after_timeout', false),
    
    createdAt: agent.createdAt ?? null,
    updatedAt: agent.updatedAt ?? null
  };

  return { formState, extraSettings };
}

